import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { api, describeError, setAuthToken } from "@/lib/api";
import { toast } from "sonner";

// "Delete account" at the bottom of the profile (Apple requires players to
// be able to delete their account in the app). Asks for the password again;
// the server removes everything (backend/account_reset.py delete_own_account).
export default function DeleteAccount() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const close = (o) => {
    if (busy) return;
    setOpen(o);
    if (!o) { setPassword(""); setError(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/me/delete", { password });
      setAuthToken(null);
      setUser(null);
      setOpen(false);
      toast.success("Your account has been deleted.");
      navigate("/", { replace: true });
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 bg-surface border border-border rounded-2xl p-6" data-testid="delete-account-card">
      <h3 className="font-display text-xl font-bold tracking-tight mb-2">Delete account</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-prose">
        Deletes your account, rating, friends and messages for good. Games you are playing now end, and your past
        games show "Deleted player" to your opponents. This can't be undone.
      </p>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
        data-testid="delete-account-button"
      >
        Delete account
      </Button>

      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This can't be undone. Enter your password to confirm.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Input
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-11"
              data-testid="delete-account-password"
            />
            {error && (
              <div className="text-sm text-destructive" data-testid="delete-account-error">{error}</div>
            )}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={() => close(false)} disabled={busy} className="rounded-full">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || !password}
                className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                data-testid="delete-account-confirm"
              >
                {busy ? "Deleting…" : "Delete for good"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
